import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Badge,
  Collapse,
  Table
} from 'react-bootstrap';
import { 
  FaArrowLeft, 
  FaPlay, 
  FaHistory, 
  FaInfoCircle,
  FaSpinner,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaCode,
  FaChevronDown,
  FaChevronRight,
  FaServer,
  FaList,
  FaBrain,
  FaTimes,
  FaWrench,
  FaSync,
  FaStop
} from 'react-icons/fa';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import ExecutionLogsWindow from '../components/ExecutionLogsWindow';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AgentTestingPage.css';

/**
 * Get default value for a field based on its type
 */
const getDefaultValueForType = (fieldType, fieldSchema) => {
  switch (fieldType) {
    case 'object':
      return {};
    case 'array':
      return [];
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return fieldSchema.default !== undefined ? fieldSchema.default : 0;
    case 'string':
    default:
      return fieldSchema.default !== undefined ? fieldSchema.default : '';
  }
};

/**
 * Initialize nested object with proper defaults based on field types
 */
const initializeNestedObject = (properties, required = []) => {
  const nestedObj = {};
  
  Object.keys(properties).forEach(nestedKey => {
    const nestedProp = properties[nestedKey];
    nestedObj[nestedKey] = getDefaultValueForType(nestedProp.type, nestedProp);
  });
  
  return nestedObj;
};

/**
 * Safe render utility to ensure objects are never passed directly to React
 */
const safeRenderValue = (value) => {
  if (value === null || value === undefined) {
    return 'No data available';
  }
  
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return '[Object - Cannot display]';
    }
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  // For any other type, convert to string
  return String(value);
};

/**
 * Parse task executions from live logs data
 */
const parseTaskExecutions = (liveLogs) => {
  try {
    if (!liveLogs) return [];
    
    // Check for executionData structure
    if (liveLogs.executionData && 
        liveLogs.executionData.current_state && 
        liveLogs.executionData.current_state.task_executions) {
      return liveLogs.executionData.current_state.task_executions;
    }
    
    // Check if liveLogs itself contains task_executions
    if (liveLogs.current_state && liveLogs.current_state.task_executions) {
      return liveLogs.current_state.task_executions;
    }
    
    // Check if task_executions is at the root level
    if (liveLogs.task_executions && Array.isArray(liveLogs.task_executions)) {
      return liveLogs.task_executions;
    }
    
    return [];
  } catch (error) {
    console.error('Error parsing task executions:', error);
    return [];
  }
};

/**
 * Format duration in seconds to human readable format
 */
const formatDuration = (duration) => {
  if (!duration) return 'N/A';
  
  if (duration < 60) {
    return `${duration.toFixed(2)}s`;
  } else if (duration < 3600) {
    const minutes = Math.floor(duration / 60);
    const seconds = (duration % 60).toFixed(1);
    return `${minutes}m ${seconds}s`;
  } else {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = (duration % 60).toFixed(1);
    return `${hours}h ${minutes}m ${seconds}s`;
  }
};

/**
 * Get badge variant for task status
 */
const getTaskStatusVariant = (status) => {
  if (!status) return 'secondary';
  
  const statusUpper = status.toUpperCase();
  switch (statusUpper) {
    case 'SUCCEEDED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'RUNNING':
    case 'IN_PROGRESS':
      return 'primary';
    case 'ABORTED':
    case 'TIMED_OUT':
      return 'warning';
    default:
      return 'secondary';
  }
};

/**
 * Format timestamp to readable format
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString();
  } catch (error) {
    return 'Invalid timestamp';
  }
};

/**
 * Truncated text component for table display
 */
const TruncatedText = ({ text, maxLength = 100 }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!text) return <span className="text-muted">No data available</span>;
  
  const textStr = safeRenderValue(text);
  
  if (textStr.length <= maxLength) {
    return <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.8rem' }}>{textStr}</pre>;
  }
  
  return (
    <div>
      <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.8rem' }}>
        {expanded ? textStr : `${textStr.substring(0, maxLength)}...`}
      </pre>
      <Button 
        variant="link" 
        size="sm" 
        className="p-0 mt-1 text-decoration-none"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'Show less' : 'Show more'}
      </Button>
    </div>
  );
};

/**
 * UPDATED: Render the task executions table with micro-agent number/service name column and increased vertical spacing
 */
const TaskExecutionsTable = ({ taskExecutions }) => {
  const [expandedRows, setExpandedRows] = useState({});
  
  const toggleRow = (taskNumber) => {
    setExpandedRows(prev => ({
      ...prev,
      [taskNumber]: !prev[taskNumber]
    }));
  };
  
  // ADDED: Helper function to get the resource identifier
  const getResourceIdentifier = (task) => {
    if (task.resource_type === 'agent') {
      return task.agent_id ? `Agent ${task.agent_id}` : 'Unknown Agent';
    } else if (task.resource_type === 'lambda') {
      return task.function_id ? task.function_id : 'Unknown Function';
    } else if (task.resource_type === 'micro_service') {
      return task.service_id ? task.service_id : (task.agent_id ? `Service ${task.agent_id}` : 'Unknown Service');
    }
    return 'Unknown Resource';
  };
  
  if (!taskExecutions || taskExecutions.length === 0) {
    return (
      <div className="text-center py-4">
        <FaInfoCircle className="text-muted mb-2" size={24} />
        <p className="text-muted">No task executions found yet. Tasks will appear as they are processed.</p>
      </div>
    );
  }
  
  // Sort by task number
  const sortedTasks = [...taskExecutions].sort((a, b) => (a.task_number || 0) - (b.task_number || 0));
  
  return (
    <div>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <h6 className="mb-0" style={{ paddingLeft: '12px' }}>Task Executions ({taskExecutions.length})</h6>
        <small className="text-muted">Click rows to expand details</small>
      </div>
      
      <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
        <Table responsive hover className="mb-0">
          <thead className="table-light sticky-top">
            <tr>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '80px', paddingLeft: '12px' }}>Task #</th>
              <th style={{ width: '120px', paddingLeft: '12px' }}>Resource Type</th>
              <th style={{ width: '150px', paddingLeft: '12px' }}>Agent/Service</th>
              <th style={{ width: '120px', paddingLeft: '12px' }}>Payload</th>
              <th style={{ width: '150px', paddingLeft: '12px' }}>Agent Output</th>
              <th style={{ width: '150px', paddingLeft: '12px' }}>Execution Summary</th>
              <th style={{ width: '80px', paddingLeft: '12px' }}>Status</th>
              <th style={{ width: '80px', paddingLeft: '12px' }}>Duration</th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => {
              const isExpanded = expandedRows[task.task_number];
              
              return (
                <React.Fragment key={task.task_number}>
                  <tr 
                    onClick={() => toggleRow(task.task_number)}
                    style={{ cursor: 'pointer', height: '80px' }}
                    className={isExpanded ? 'table-active' : ''}
                  >
                    <td className="text-center align-middle" style={{ padding: '15px 8px' }}>
                      {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <Badge bg="info" className="fw-bold fs-6 px-3 py-2">
                        {task.task_number}
                      </Badge>
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <div className="d-flex flex-column align-items-center">
                        <Badge 
                          bg={task.resource_type === 'lambda' ? 'warning' : task.resource_type === 'agent' ? 'primary' : 'success'} 
                          className="mb-1 small px-2 py-1"
                        >
                          {task.resource_type === 'lambda' ? 'Lambda' : 
                           task.resource_type === 'agent' ? 'Agent' : 
                           task.resource_type === 'micro_service' ? 'Service' : 
                           task.resource_type}
                        </Badge>
                      </div>
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <div className="d-flex flex-column align-items-center">
                        <div className="fw-bold text-center mb-1" style={{ fontSize: '0.9rem' }}>
                          {getResourceIdentifier(task)}
                        </div>
                        {(task.agent_id || task.function_id) && (
                          <small className="text-muted text-center">
                            ID: {task.agent_id || task.function_id}
                          </small>
                        )}
                      </div>
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <div style={{ maxHeight: '100px', overflow: 'hidden' }}>
                        <TruncatedText text={task.payload} maxLength={50} />
                      </div>
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <div style={{ maxHeight: '100px', overflow: 'hidden' }}>
                        <TruncatedText text={task.agentOutput} maxLength={70} />
                      </div>
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <div style={{ maxHeight: '100px', overflow: 'hidden' }}>
                        <TruncatedText text={task.executionSummary} maxLength={70} />
                      </div>
                    </td>
                    <td className="align-middle" style={{ padding: '15px 12px' }}>
                      <div className="d-flex flex-column align-items-center">
                        <Badge bg={getTaskStatusVariant(task.status)} className="px-2 py-1">
                          {task.status}
                        </Badge>
                        {task.statusCode && (
                          <small className="text-muted mt-1">
                            {task.statusCode}
                          </small>
                        )}
                      </div>
                    </td>
                    <td className="text-center align-middle" style={{ padding: '15px 12px' }}>
                      {formatDuration(task.duration)}
                    </td>
                  </tr>
                  
                  {/* Expanded row details with increased padding */}
                  {isExpanded && (
                    <tr>
                      <td colSpan="9" className="p-0">
                        <div className="p-4 bg-light border-top">
                          <Row>
                            <Col md={6}>
                              <div className="mb-4">
                                <h6 className="text-primary mb-3" style={{ paddingLeft: '12px' }}>Task Details</h6>
                                <div className="small">
                                  <div className="mb-2" style={{ paddingLeft: '12px' }}><strong>Resource Type:</strong> {task.resource_type}</div>
                                  <div className="mb-2" style={{ paddingLeft: '12px' }}>
                                    <strong>Resource Identifier:</strong> {getResourceIdentifier(task)}
                                  </div>
                                  {task.agent_id && (
                                    <div className="mb-2" style={{ paddingLeft: '12px' }}><strong>Agent ID:</strong> {task.agent_id}</div>
                                  )}
                                  {task.function_id && (
                                    <div className="mb-2" style={{ paddingLeft: '12px' }}><strong>Function ID:</strong> {task.function_id}</div>
                                  )}
                                  <div className="mb-2" style={{ paddingLeft: '12px' }}><strong>Start Time:</strong> {formatTimestamp(task.start_time)}</div>
                                  {task.end_time && (
                                    <div className="mb-2" style={{ paddingLeft: '12px' }}><strong>End Time:</strong> {formatTimestamp(task.end_time)}</div>
                                  )}
                                </div>
                              </div>
                              
                              {task.execution_arn && (
                                <div className="mb-4">
                                  <h6 className="text-info mb-3" style={{ paddingLeft: '12px' }}>Execution ARN</h6>
                                  <div className="p-3 bg-white rounded border">
                                    <small style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                      {task.execution_arn}
                                    </small>
                                  </div>
                                </div>
                              )}
                            </Col>
                            
                            <Col md={6}>
                              {task.payload && (
                                <div className="mb-4">
                                  <h6 className="text-warning mb-3" style={{ paddingLeft: '12px' }}>Full Payload</h6>
                                  <div className="p-3 bg-white rounded border" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <pre className="mb-0" style={{ fontSize: '0.8rem' }}>
                                      {safeRenderValue(task.payload)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                            </Col>
                          </Row>
                          
                          {task.agentOutput && (
                            <div className="mb-4">
                              <h6 className="text-success mb-3" style={{ paddingLeft: '12px' }}>Full Agent Output</h6>
                              <div className="p-4 bg-white rounded border" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <pre className="mb-0" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {safeRenderValue(task.agentOutput)}
                                </pre>
                              </div>
                            </div>
                          )}
                          
                          {task.executionSummary && (
                            <div className="mb-3">
                              <h6 className="text-info mb-3" style={{ paddingLeft: '12px' }}>Full Execution Summary</h6>
                              <div className="p-4 bg-white rounded border" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <pre className="mb-0" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {safeRenderValue(task.executionSummary)}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      
      <div className="mt-3 text-center">
        <small className="text-muted">
          Updated at {new Date().toLocaleTimeString()} - Click sections to expand/collapse
        </small>
      </div>
    </div>
  );
};

const MiniAgentTestingPage = () => {
  const { agentId, microAgentId, miniAgentId } = useParams();
  const navigate = useNavigate();
  
  // Determine the agent ID based on the URL parameters
  const targetId = microAgentId || miniAgentId;
  const agentType = microAgentId ? 'micro-agent' : 'mini-agent';
  const agentTypeLabel = microAgentId ? 'Micro-Agent' : 'Mini-Agent';
  
  // State variables
  const [agent, setAgent] = useState(null);
  const [parentAgent, setParentAgent] = useState(null);
  const [schema, setSchema] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [executingEnvironment, setExecutingEnvironment] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [executionArn, setExecutionArn] = useState(null);
  const [environment, setEnvironment] = useState('development');
  const [formErrors, setFormErrors] = useState({});
  const [liveLogs, setLiveLogs] = useState(null);
  const [fetchingLogs, setFetchingLogs] = useState(false);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(false);
  
  // New state for controlling polling behavior
  const [statusPollingActive, setStatusPollingActive] = useState(true);
  const [logsPollingActive, setLogsPollingActive] = useState(true);
  
  // State for detailed execution logs (simplified - no longer using same API as LogsReview.js)
  const [executionDetails, setExecutionDetails] = useState(null);
  const [loadingExecutionDetails, setLoadingExecutionDetails] = useState(false);
  
  // State for parsed execution response fields - FIXED: Ensure these are always strings
  const [bodyMessage, setBodyMessage] = useState(null);
  const [bodyExecutionArn, setBodyExecutionArn] = useState(null);

  // NEW: State for parsed live logs output fields - FIXED: Ensure proper string handling
  const [parsedExecutionSummary, setParsedExecutionSummary] = useState(null);
  const [parsedAgentOutput, setParsedAgentOutput] = useState(null);

  // NEW: State for execution analysis functionality - CHANGED to use card/box approach
  const [showAnalysisSection, setShowAnalysisSection] = useState(false);
  const [fetchingAnalysis, setFetchingAnalysis] = useState(false);
  const [analysisExecutionArn, setAnalysisExecutionArn] = useState(null);
  const [analysisPollingActive, setAnalysisPollingActive] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [fetchingAnalysisStatus, setFetchingAnalysisStatus] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  // CHANGED: State for Quick Edit/Fix card section functionality (converted from modal)
  const [showQuickEditSection, setShowQuickEditSection] = useState(false);
  const [quickEditInstructions, setQuickEditInstructions] = useState('');
  const [includeExecutionAnalysis, setIncludeExecutionAnalysis] = useState(false);
  const [submittingQuickEdit, setSubmittingQuickEdit] = useState(false);
  const [quickEditResult, setQuickEditResult] = useState(null);

  // NEW: State for data restored from LogsReview
  const [restoredFromLogs, setRestoredFromLogs] = useState(false);

  // State for stopping execution
  const [stoppingExecution, setStoppingExecution] = useState(false);

  // Fetch agent details and schema on load
  useEffect(() => {
    fetchAgentDetails();
    fetchSchema();
  }, [targetId, agentType]);

  // NEW: Parse live logs when they update
  useEffect(() => {
    if (liveLogs) {
      parseLiveLogsOutput(liveLogs);
    }
  }, [liveLogs]);

  // UPDATED: Watch for status changes to stop polling when execution completes
  useEffect(() => {
    let timeoutId = null;
    
    if (executionStatus && (
      executionStatus.status === 'SUCCEEDED' || 
      executionStatus.status === 'FAILED' ||
      executionStatus.status === 'COMPLETED'
    )) {
      if (statusPollingActive || logsPollingActive) {
        console.log(`Execution ${executionStatus.status} - stopping all polling`);
        setStatusPollingActive(false);
        setLogsPollingActive(false);
        
        // Get final execution details after 10 second delay if SUCCEEDED
        if (executionArn && executionStatus.status === 'SUCCEEDED') {
          console.log('Execution SUCCEEDED - waiting 10 seconds before retrieving execution details');
          timeoutId = setTimeout(() => {
            console.log('10 second delay completed - now fetching execution details');
            fetchBasicExecutionDetails(executionArn);
          }, 10000);
        }
      }
    }
    
    // Cleanup function to clear timeout if component unmounts or dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        console.log('Cleared timeout for execution details fetch');
      }
    };
  }, [executionStatus, statusPollingActive, logsPollingActive, executionArn]);

  // NEW: Watch for analysis status changes to stop analysis polling when analysis completes
  useEffect(() => {
    if (analysisStatus && (
      analysisStatus.status === 'SUCCEEDED' || 
      analysisStatus.status === 'FAILED' ||
      analysisStatus.status === 'COMPLETED'
    )) {
      if (analysisPollingActive) {
        console.log(`Analysis execution ${analysisStatus.status} - stopping analysis polling`);
        setAnalysisPollingActive(false);
        
        // If analysis succeeded, fetch the final result
        if (analysisExecutionArn && analysisStatus.status === 'SUCCEEDED') {
          console.log('Analysis SUCCEEDED - fetching final analysis result');
          setTimeout(() => {
            fetchAnalysisResult(analysisExecutionArn);
          }, 5000);
        }
      }
    }
  }, [analysisStatus, analysisPollingActive, analysisExecutionArn]);

  // NEW: Function to parse live logs output for mini-agents (different endpoint)
  const parseLiveLogsOutput = (logs) => {
    try {
      console.log('Parsing live logs for output fields:', logs);
      
      let executionSummary = null;
      let agentOutput = null;
      
      if (logs && typeof logs === 'object') {
        // Check for executionData structure from mini-agent endpoint
        if (logs.executionData && logs.executionData.current_state && logs.executionData.current_state.task_executions) {
          const taskExecutions = logs.executionData.current_state.task_executions;
          
          // Find the latest task execution with agentOutput
          for (const task of taskExecutions.reverse()) {
            if (task.agentOutput) {
              agentOutput = task.agentOutput;
              break;
            }
          }
          
          // Use the execution summary from the latest task if available
          const latestTask = taskExecutions[taskExecutions.length - 1];
          if (latestTask && latestTask.executionSummary) {
            executionSummary = latestTask.executionSummary;
          }
        }
        
        // Fallback to existing parsing logic for compatibility
        else if (logs.output && logs.output.agentOutput && logs.output.executionSummary) {
          agentOutput = logs.output.agentOutput;
          executionSummary = logs.output.executionSummary;
        }
        else if (logs.Output && logs.Output.agentOutput && logs.Output.executionSummary) {
          agentOutput = logs.Output.agentOutput;
          executionSummary = logs.Output.executionSummary;
        }
      }
      
      // FIXED: Ensure values are always safe for React rendering
      if (executionSummary !== null) {
        const safeExecutionSummary = safeRenderValue(executionSummary);
        setParsedExecutionSummary(safeExecutionSummary);
        console.log('Parsed execution summary (safe):', typeof safeExecutionSummary, safeExecutionSummary.substring(0, 100));
      }
      
      if (agentOutput !== null) {
        const safeAgentOutput = safeRenderValue(agentOutput);
        setParsedAgentOutput(safeAgentOutput);
        console.log('Parsed agent output (safe):', typeof safeAgentOutput, safeAgentOutput.substring(0, 100));
      }
      
    } catch (error) {
      console.error('Error parsing live logs output:', error);
      // Set safe fallback values
      setParsedExecutionSummary('Error parsing execution summary');
      setParsedAgentOutput('Error parsing agent output');
    }
  };

  // NEW: Fetch live logs from the mini-agent specific endpoint
  const fetchLiveLogs = async (executionArn) => {
    if (!executionArn) return;

    try {
      setFetchingLogs(true);

      // Use the mini-agent specific endpoint
      const payload = {
        executionArn: executionArn
      };

      console.log('Fetching mini-agent execution logs with payload:', payload);

      // Make the API call to the mini-agent endpoint
      const response = await api.makeAuthenticatedRequest('POST', '/mini-agent/retrieve-mini-agent-execution', payload);
      
      console.log('Mini-agent logs response:', response);
      
      // Set the complete response as live logs
      setLiveLogs(response);
      
      setFetchingLogs(false);
    } catch (err) {
      console.error('Error fetching mini-agent logs:', err);
      setFetchingLogs(false);
    }
  };

  // Simplified function to get basic execution details
  const fetchBasicExecutionDetails = async (executionArn) => {
    if (!executionArn) return;
    
    setLoadingExecutionDetails(true);
    
    try {
      console.log(`Fetching basic execution details for: ${executionArn}`);
      
      // Use simple status check
      const agentNumber = agent?.agentNumber || '';
      if (agentNumber && agentNumber.length === 3) {
        const payload = {
          agentNumber: agentNumber,
          awsAccountNumber: parentAgent?.awsAccount || "851725399969",
          awsRegion: parentAgent?.awsRegion || "us-west-2",
          executionArn: executionArn,
          parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
          environment: executingEnvironment
        };
        
        const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}/status`, payload);
        
        if (response && response.response && response.response.body) {
          let statusData = response.response.body;
          if (typeof statusData === 'string') {
            try {
              statusData = JSON.parse(statusData);
            } catch (parseError) {
              console.error('Error parsing status response:', parseError);
            }
          }
          
          setExecutionDetails({
            status: statusData?.status || 'COMPLETED',
            message: 'Execution completed successfully',
            executionArn: executionArn
          });
        }
      }
      
    } catch (err) {
      console.error('Error fetching basic execution details:', err);
      // Don't show error, just log it
    } finally {
      setLoadingExecutionDetails(false);
    }
  };

  // UPDATED: Handle analyze execution using micro-agent 128 instead of 111
  const handleAnalyzeExecution = async () => {
    if (!executionArn) return;

    setFetchingAnalysis(true);
    setAnalysisResult(null);
    setAnalysisStatus(null);
    setShowAnalysisSection(true);

    try {
      console.log('Starting execution analysis for original executionArn:', executionArn);

      // UPDATED: Use micro-agent 128 instead of 111
      const response = await api.testAgent(
        agentId,           // Parent agent ID (AgentDeveloper)
        'micro-agent',     // Agent 128 is a micro-agent
        '12',             // UPDATED: Use agent 128 instead of 111
        { executionArn: executionArn },  // The payload - just the executionArn
        'production'       // Always run in production
      );

      console.log('Analysis execution response:', response);

      // Extract execution ARN from response (same structure as normal execution)
      let analysisArn = null;
      if (response && response.body && typeof response.body === 'string') {
        try {
          const parsedBody = JSON.parse(response.body);
          analysisArn = parsedBody.executionArn;
        } catch (parseError) {
          console.error('Error parsing analysis response body:', parseError);
        }
      } else if (response && response.body && response.body.executionArn) {
        analysisArn = response.body.executionArn;
      } else if (response && response.executionArn) {
        analysisArn = response.executionArn;
      }

      if (analysisArn) {
        console.log('Analysis execution ARN (from agent 128):', analysisArn);
        setAnalysisExecutionArn(analysisArn);
        setAnalysisPollingActive(true);
      
        // Start polling for analysis status
        fetchAnalysisStatus(analysisArn);
        toast.success('Analysis execution started successfully');
      } else {
        throw new Error('No execution ARN received from analysis agent');
      }
    
    } catch (err) {
      console.error('Error starting execution analysis:', err);
      setAnalysisResult({
        success: false,
        error: `Failed to start analysis: ${err.message}`
      });
    } finally {
      setFetchingAnalysis(false);
    }
  };

  // NEW: Function to fetch analysis execution status using agent 128
  const fetchAnalysisStatus = async (analysisArn) => {
    if (!analysisArn) return;
    
    try {
      setFetchingAnalysisStatus(true);
      
      console.log('Checking analysis execution status for:', analysisArn);
      
      // UPDATED: Use agent 128 for status checking
      const payload = {
        agentNumber: "128",
        awsAccountNumber: parentAgent?.awsAccount || "851725399969",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        executionArn: analysisArn,
        parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
        environment: "production"
      };
      
      // Make the API call to check analysis status
      const response = await api.makeAuthenticatedRequest('POST', `/agents/128/status`, payload);
      
      if (response && response.response && response.response.body) {
        // Parse the body if it's a string
        if (typeof response.response.body === 'string') {
          try {
            const statusData = JSON.parse(response.response.body);
            setAnalysisStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing analysis status response body:', parseError);
            setAnalysisStatus(response.response.body);
          }
        } else {
          setAnalysisStatus(response.response.body);
        }
      } else if (response && response.body) {
        // Parse the body if it's a string
        if (typeof response.body === 'string') {
          try {
            const statusData = JSON.parse(response.body);
            setAnalysisStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing analysis status response body:', parseError);
            setAnalysisStatus(response.body);
          }
        } else {
          setAnalysisStatus(response.body);
        }
      } else {
        console.warn('Unexpected analysis status response format:', response);
        setAnalysisStatus(null);
      }
      
      setFetchingAnalysisStatus(false);
    } catch (err) {
      console.error('Error fetching analysis execution status:', err);
      setFetchingAnalysisStatus(false);
    }
  };

  // Function to fetch the final analysis result using agent 128
  const fetchAnalysisResult = async (analysisArn) => {
    if (!analysisArn) return;

    try {
      console.log('Fetching final analysis result for:', analysisArn);

      // UPDATED: Use agent 128 logs endpoint to get the final result
      const payload = {
        agentNumber: "128",
        awsAccountNumber: parentAgent?.awsAccount || "851725399969",
        awsRegion: parentAgent?.awsRegion || "us-west-2", 
        executionArn: analysisArn,
        parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
        environment: "production"
      };

      const response = await api.makeAuthenticatedRequest('POST', `/agents/128/log`, payload);

      console.log('Analysis logs response:', response);

      let logsData = null;
      
      if (response && response.response && response.response.body) {
        let responseBody = response.response.body;
        
        // If body is a string, parse it
        if (typeof responseBody === 'string') {
          try {
            responseBody = JSON.parse(responseBody);
          } catch (parseError) {
            console.error('Error parsing analysis logs response body:', parseError);
          }
        }
        
        logsData = responseBody;
      } else if (response && response.body) {
        if (typeof response.body === 'string') {
          try {
            logsData = JSON.parse(response.body);
          } catch (parseError) {
            console.error('Error parsing response body:', parseError);
            logsData = response.body;
          }
        } else {
          logsData = response.body;
        }
      } else if (response && response.logs) {
        logsData = response.logs;
      }

      console.log('Analysis logs data:', logsData);

      // Extract both agentOutput and executionSummary from the analysis logs
      let agentOutput = null;
      let executionSummary = null;
      
      // Parse the logs to find both output fields
      if (logsData) {
        if (logsData.output && logsData.output.agentOutput && logsData.output.executionSummary) {
          agentOutput = logsData.output.agentOutput;
          executionSummary = logsData.output.executionSummary;
          console.log('Found both fields in output object');
        } 
        else if (logsData.Output && logsData.Output.agentOutput && logsData.Output.executionSummary) {
          agentOutput = logsData.Output.agentOutput;
          executionSummary = logsData.Output.executionSummary;
          console.log('Found both fields in Output object (capitalized)');
        }
        else if (logsData.agentOutput && logsData.executionSummary) {
          agentOutput = logsData.agentOutput;
          executionSummary = logsData.executionSummary;
          console.log('Found agentOutput and executionSummary at root level');
        }
      }

      if (agentOutput && executionSummary) {
        // FIXED: Ensure both outputs are safe for React rendering
        const safeAgentOutput = safeRenderValue(agentOutput);
        const safeExecutionSummary = safeRenderValue(executionSummary);
        
        setAnalysisResult({
          success: true,
          agentOutput: safeAgentOutput,
          executionSummary: safeExecutionSummary
        });
        console.log('Analysis completed successfully with both output fields');
      } else if (agentOutput || executionSummary) {
        // Found only one field - still consider it a success
        const safeAgentOutput = agentOutput ? safeRenderValue(agentOutput) : null;
        const safeExecutionSummary = executionSummary ? safeRenderValue(executionSummary) : null;
        
        setAnalysisResult({
          success: true,
          agentOutput: safeAgentOutput,
          executionSummary: safeExecutionSummary
        });
        console.log('Analysis completed with partial results:', { agentOutput: !!agentOutput, executionSummary: !!executionSummary });
      } else {
        console.warn('Analysis completed but neither agentOutput nor executionSummary found');
        setAnalysisResult({
          success: false,
          error: 'Analysis completed but no output fields found in the expected structure'
        });
      }

    } catch (err) {
      console.error('Error fetching analysis result:', err);
      setAnalysisResult({
        success: false,
        error: `Failed to fetch analysis result: ${err.message}`
      });
    }
  };

  // CHANGED: Handle Quick Edit/Fix submission (converted from modal to card section)
  const handleQuickEditSubmit = async () => {
    if (!quickEditInstructions.trim() || quickEditInstructions.trim().length < 10) {
      toast.error('Instructions must be at least 10 characters long');
      return;
    }

    if (!agentId || !parentAgent) {
      toast.error('Missing required agent or parent agent data');
      return;
    }

    try {
      setSubmittingQuickEdit(true);
      setQuickEditResult(null);

      // Build the change instructions by starting with user input
      let changeInstructions = quickEditInstructions.trim();

      // Append execution analysis if requested and available
      if (includeExecutionAnalysis && analysisResult && analysisResult.agentOutput) {
        changeInstructions += '\n\nExecution Analysis Context:\n';
        changeInstructions += safeRenderValue(analysisResult.agentOutput);
      }

      // Build the GitHub repo string from parent agent metadata
      const githubAccount = parentAgent.repoAccount || parentAgent.github_account;
      const githubRepo = parentAgent.repoName || parentAgent.github_repo;
      
      if (!githubAccount || !githubRepo) {
        throw new Error('GitHub repository information not available in parent agent metadata');
      }

      const githubRepoString = `${githubAccount}/${githubRepo}`;

      // Get the current agent's micro_agent_number for the payload
      console.log('Getting micro_agent_number for current agent to use in payload...');
      
      // Call the db/micro-agents endpoint with both agent_id and micro_agent_id
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agents', {
        agent_id: "1",
        micro_agent_id: targetId
      });
      
      console.log('Raw response from db/micro-agents:', response);
      
      // Extract micro_agents array from the nested response structure
      const microAgents = extractFromNestedResponse(response, 'data', 'micro_agents');
      
      if (!microAgents || !Array.isArray(microAgents) || microAgents.length === 0) {
        throw new Error('No micro-agents found in response');
      }
      
      console.log('Found micro-agents:', microAgents.length);
      
      // Since we called with micro_agent_id, we should get back just the current agent
      const currentMicroAgent = microAgents[0];

      if (!currentMicroAgent) {
        throw new Error(`Current micro-agent with ID ${targetId} not found`);
      }

      const currentMicroAgentNumber = currentMicroAgent.micro_agent_number;
      console.log('Found current agent micro_agent_number:', currentMicroAgentNumber);

      // Now find micro-agent 104
      const allAgentsResponse = await api.makeAuthenticatedRequest('POST', '/db/micro-agents', {
        agent_id: "1"
      });

      const allMicroAgents = extractFromNestedResponse(allAgentsResponse, 'data', 'micro_agents');
      
      if (!allMicroAgents || !Array.isArray(allMicroAgents) || allMicroAgents.length === 0) {
        throw new Error('No micro-agents found when searching for agent 104');
      }

      const microAgent104 = allMicroAgents.find(ma => 
        String(ma.micro_agent_number) === "104"
      );

      if (!microAgent104) {
        throw new Error('Micro-agent with number 104 not found');
      }

      console.log('Found micro-agent 104:', microAgent104);
      const microAgent104Id = microAgent104.micro_agent_id;

      // Prepare payload for micro-agent 104
      const payload = {
        AgentNumber: currentMicroAgentNumber,
        ParentAgent: {
          name: parentAgent.name || parentAgent.agentName,
          githubRepo: githubRepoString
        },
        ChangeInstructions: changeInstructions,
      };

      console.log('Calling micro-agent 104 with payload:', payload);
      console.log('Using micro_agent_id:', microAgent104Id);

      // Call micro-agent 104 in production mode
      const quickEditResponse = await api.testAgent(
        agentId,
        'micro-agent',
        microAgent104Id,
        payload,
        'production'
      );

      console.log('Quick edit execution response:', quickEditResponse);

      if (quickEditResponse) {
        setQuickEditResult({
          success: true,
          message: 'Quick edit/fix request has been executed successfully!',
          executionArn: quickEditResponse.executionArn || quickEditResponse.body?.executionArn,
          response: quickEditResponse
        });
        
        // Clear the form
        setQuickEditInstructions('');
        setIncludeExecutionAnalysis(false);
        
        toast.success('Quick edit/fix executed successfully!');
        
        // Auto-hide section after 10 seconds
        setTimeout(() => {
          setShowQuickEditSection(false);
          setQuickEditResult(null);
        }, 10000);
      } else {
        throw new Error('No response received from quick edit agent');
      }

    } catch (err) {
      console.error('Error submitting quick edit:', err);
      setQuickEditResult({
        success: false,
        error: err.message || 'Failed to submit quick edit request'
      });
      toast.error('Failed to submit quick edit request: ' + err.message);
    } finally {
      setSubmittingQuickEdit(false);
    }
  };

  // Enhanced function to extract data from nested response structures
  const extractFromNestedResponse = (response, ...keys) => {
    if (!response) return null;
    
    console.log('Extracting from nested response structure:', Object.keys(response));
    
    // Handle the test_results structure first
    if (response.body?.data?.test_results?.endpoint_response?.body) {
      try {
        console.log('Found test_results structure with string body, parsing...');
        const parsedBody = typeof response.body.data.test_results.endpoint_response.body === 'string' 
          ? JSON.parse(response.body.data.test_results.endpoint_response.body)
          : response.body.data.test_results.endpoint_response.body;
        
        console.log('Successfully parsed test_results body:', parsedBody);
        
        // Now extract the requested keys from the parsed body
        let value = parsedBody;
        for (const key of keys) {
          if (value && typeof value === 'object' && key in value) {
            value = value[key];
          } else {
            return null;
          }
        }
        return value;
      } catch (parseError) {
        console.warn('Failed to parse test_results body:', parseError);
      }
    }
    
    if (response.data?.test_results?.endpoint_response?.body) {
      try {
        console.log('Found test_results structure in data, parsing...');
        const parsedBody = typeof response.data.test_results.endpoint_response.body === 'string' 
          ? JSON.parse(response.data.test_results.endpoint_response.body)
          : response.data.test_results.endpoint_response.body;
        
        console.log('Successfully parsed test_results body from data:', parsedBody);
        
        // Now extract the requested keys from the parsed body
        let value = parsedBody;
        for (const key of keys) {
          if (value && typeof value === 'object' && key in value) {
            value = value[key];
          } else {
            return null;
          }
        }
        return value;
      } catch (parseError) {
        console.warn('Failed to parse test_results body from data:', parseError);
      }
    }
    
    // Fallback to direct extraction
    let value = response;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }
    return value;
  };

  // Helper function to check if field name requires large text box
  const isLargeTextField = (fieldName) => {
    const lowerFieldName = fieldName.toLowerCase().replace(/[_\s]/g, '');
    // UPDATED: Made text boxes bigger for main task and context fields
    const largeTextFields = ['instructions', 'architecture', 'description', 'task', 'maintask', 'main_task', 'context'];
    return largeTextFields.some(field => lowerFieldName.includes(field.replace(/[_\s]/g, '')));
  };

  // Helper function to flatten nested objects into form fields
  const flattenObjectToFields = (obj, prefix = '', properties = {}, required = []) => {
    const fields = [];
    
    Object.keys(obj).forEach(key => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const propSchema = properties[key] || {};
      const value = obj[key];
      
      // Check if this is a nested object (but not an array)
      if (value && typeof value === 'object' && !Array.isArray(value) && propSchema.type === 'object') {
        // Add a visual separator/grouping for nested objects
        fields.push({
          name: fullPath,
          label: propSchema.title || key,
          type: 'object',
          isGroup: true,
          description: propSchema.description,
          required: required?.includes(key)
        });
        
        // Recursively flatten nested properties
        const nestedProps = propSchema.properties || {};
        const nestedRequired = propSchema.required || [];
        const nestedFields = flattenObjectToFields(value, fullPath, nestedProps, nestedRequired);
        fields.push(...nestedFields);
      } else {
        // Regular field - Include parent name in label if nested
        let fieldLabel = propSchema.title || key;
        
        // If this is a nested field (has a prefix), include parent name in label
        if (prefix) {
          const parentParts = prefix.split('.');
          const parentName = parentParts[parentParts.length - 1];
          const parentLabel = parentName.charAt(0).toUpperCase() + parentName.slice(1).replace(/_/g, ' ');
          const childLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1).replace(/_/g, ' ');
          fieldLabel = `${parentLabel} - ${childLabel}`;
        }
        
        fields.push({
          name: fullPath,
          label: fieldLabel,
          type: propSchema.type || (Array.isArray(value) ? 'array' : typeof value),
          format: propSchema.format,
          description: propSchema.description,
          required: required?.includes(key),
          default: propSchema.default,
          enum: propSchema.enum,
          options: propSchema.options
        });
      }
    });
    
    return fields;
  };

  // Fetch the agent schema from the db/micro-agents endpoint
  const fetchSchema = async () => {
    try {
      setLoading(true);
      console.log('Fetching schema from db/micro-agents endpoint for targetId:', targetId);
      
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agents', {
        agent_id: "1",
        micro_agent_id: targetId
      });
      
      console.log('Raw response from db/micro-agents:', response);
      
      // Extract micro_agents array from the nested response structure
      const microAgents = extractFromNestedResponse(response, 'data', 'micro_agents');
      
      if (!microAgents || !Array.isArray(microAgents) || microAgents.length === 0) {
        throw new Error('No micro-agents found in response');
      }
      
      console.log('Found micro-agents:', microAgents.length);
      
      const microAgent = microAgents[0];
      
      if (!microAgent) {
        throw new Error(`Micro-agent with ID ${targetId} not found`);
      }
      
      console.log('Found target micro-agent:', microAgent);
      
      // Extract and parse the schema field
      if (microAgent.schema) {
        let schemaObject;
        try {
          schemaObject = JSON.parse(microAgent.schema);
          console.log('Parsed schema object:', schemaObject);
        } catch (parseError) {
          console.error('Error parsing schema JSON:', parseError);
          throw new Error('Invalid schema format');
        }
        
        // Use the input schema for form generation
        const inputSchema = schemaObject.input;
        
        if (inputSchema && inputSchema.properties) {
          setSchema(inputSchema);
          
          // Initialize form data with proper default values based on field types
          const initialData = {};
          Object.keys(inputSchema.properties).forEach(key => {
            const prop = inputSchema.properties[key];
            
            // Set default value based on type to prevent validation failures
            if (prop.type === 'object' && prop.properties) {
              // Initialize nested object with proper defaults for all nested fields
              initialData[key] = initializeNestedObject(prop.properties, prop.required);
            } else {
              // Use type-appropriate default value
              initialData[key] = getDefaultValueForType(prop.type, prop);
            }
          });
          
          console.log('Initial form data with type-safe defaults:', initialData);
          
          // Flatten nested objects into individual fields
          const fields = flattenObjectToFields(
            initialData, 
            '', 
            inputSchema.properties, 
            inputSchema.required
          );
          
          setFormFields(fields);
          setFormData(initialData);
          
          console.log('Schema successfully processed. Form fields:', fields.length);
        } else {
          throw new Error('No input properties found in schema');
        }
      } else {
        throw new Error('No schema field found in micro-agent data');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching schema:', err);
      setError('Failed to load agent schema: ' + err.message);
      setLoading(false);
    }
  };

  // Effect to check for saved execution payload and restore it
  useEffect(() => {
    if (formFields.length > 0) {
      try {
        const savedExecutionJSON = localStorage.getItem('retryExecutionPayload');
        if (savedExecutionJSON) {
          const savedExecution = JSON.parse(savedExecutionJSON);
          console.log('Found saved execution context to restore:', savedExecution);
          
          // Set the environment if it exists in the payload
          if (savedExecution.environment) {
            setEnvironment(savedExecution.environment);
            setExecutingEnvironment(savedExecution.environment);
          }

          // Apply the payload data to the form
          const newFormData = {...formData};
          
          // Process the payload into the form
          const payload = savedExecution.payload;
          if (typeof payload === 'object') {
            // Deep merge the payload into form data, handling nested objects
            const mergePayload = (target, source) => {
              Object.keys(source).forEach(key => {
                if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                  if (!target[key]) target[key] = {};
                  mergePayload(target[key], source[key]);
                } else {
                  target[key] = source[key];
                }
              });
            };
            
            mergePayload(newFormData, payload);
            
            // Update form data with the restored values
            setFormData(newFormData);
            console.log('Restored form data from execution payload:', newFormData);
          }

          // Restore execution context if available
          if (savedExecution.fromLogsReview) {
            console.log('Restoring execution context from LogsReview...');
            
            if (savedExecution.executionArn) {
              setExecutionArn(savedExecution.executionArn);
            }
            
            if (savedExecution.executionStatus) {
              setExecutionStatus(savedExecution.executionStatus);
              setStatusPollingActive(false);
              setLogsPollingActive(false);
            }
            
            if (savedExecution.executionResult) {
              setExecutionDetails({
                status: savedExecution.executionResult.outcome || 'COMPLETED',
                message: 'Execution data restored from logs',
                executionArn: savedExecution.executionArn
              });
              setLoadingExecutionDetails(false);
              
              if (savedExecution.executionResult.agentOutput) {
                setParsedAgentOutput(safeRenderValue(savedExecution.executionResult.agentOutput));
              }
              
              if (savedExecution.executionResult.executionLog) {
                setParsedExecutionSummary(safeRenderValue(savedExecution.executionResult.executionLog));
              }
            }
            
            if (savedExecution.liveLogs) {
              setLiveLogs(savedExecution.liveLogs);
              setFetchingLogs(false);
            }
            
            setRestoredFromLogs(true);
            toast.info(`Form and execution context populated from execution: ${savedExecution.executionId || 'previous execution'}`);
          } else {
            toast.info('Form populated with previous execution data');
          }
          
          localStorage.removeItem('retryExecutionPayload');
        }
      } catch (err) {
        console.error('Error restoring execution payload:', err);
      }
    }
  }, [formFields]);

  // Handle form field changes
  const handleInputChange = (name, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      
      // Handle nested field paths
      if (name.includes('.')) {
        const parts = name.split('.');
        let current = newData;
        
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        current[parts[parts.length - 1]] = value;
      } else {
        newData[name] = value;
      }
      
      return newData;
    });
    
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Get nested value from form data
  const getNestedValue = (obj, path) => {
    if (!path.includes('.')) {
      return obj[path];
    }
    
    const parts = path.split('.');
    let value = obj;
    
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return undefined;
      }
    }
    
    return value;
  };

  // Validate the form before submission
  const validateForm = () => {
    const errors = {};
    formFields.filter(field => field.required && !field.isGroup).forEach(field => {
      const value = getNestedValue(formData, field.name);
      if (value === undefined || value === null || value === '') {
        errors[field.name] = `${field.label || field.name} is required`;
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch execution status
  const fetchExecutionStatus = async (executionArn) => {
    if (!executionArn) return;
    
    try {
      setFetchingStatus(true);
      
      const agentNumber = agent?.agentNumber || '';
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error('Invalid agent number. Cannot fetch status.');
      }
      
      const payload = {
        agentNumber: agentNumber,
        awsAccountNumber: parentAgent?.awsAccount || "851725399969",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        executionArn: executionArn,
        parentAgentName: parentAgent?.name || agent?.parentAgentName || "AgentDeveloper",
        environment: executingEnvironment
      };
      
      const response = await api.makeAuthenticatedRequest('POST', `/agents/${agentNumber}/status`, payload);
      
      if (response && response.response && response.response.body) {
        if (typeof response.response.body === 'string') {
          try {
            const statusData = JSON.parse(response.response.body);
            setExecutionStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing status response body:', parseError);
            setExecutionStatus(response.response.body);
          }
        } else {
          setExecutionStatus(response.response.body);
        }
      } else if (response && response.body) {
        if (typeof response.body === 'string') {
          try {
            const statusData = JSON.parse(response.body);
            setExecutionStatus(statusData);
          } catch (parseError) {
            console.error('Error parsing status response body:', parseError);
            setExecutionStatus(response.body);
          }
        } else {
          setExecutionStatus(response.body);
        }
      } else {
        console.warn('Unexpected status response format:', response);
        setExecutionStatus(null);
      }
      
      setFetchingStatus(false);
    } catch (err) {
      console.error('Error fetching execution status:', err);
      setFetchingStatus(false);
    }
  };

  // Function to stop execution
  const handleStopExecution = async () => {
    if (!executionArn) return;

    try {
      setStoppingExecution(true);
      
      console.log('Stopping execution:', executionArn);
      
      const response = await api.makeAuthenticatedRequest('POST', '/execution/stop-step-function', {
        executionArn: executionArn
      });
      
      console.log('Stop execution response:', response);
      
      if (response && response.success) {
        toast.success('Execution stopped successfully');
        setStatusPollingActive(false);
        setLogsPollingActive(false);
        setTimeout(() => {
          fetchExecutionStatus(executionArn);
        }, 2000);
      } else {
        throw new Error('Failed to stop execution');
      }
    } catch (err) {
      console.error('Error stopping execution:', err);
      toast.error('Failed to stop execution: ' + err.message);
    } finally {
      setStoppingExecution(false);
    }
  };

  // Manual refresh function for when data was restored from logs
  const handleManualRefresh = async () => {
    if (!executionArn) return;
    
    console.log('Manual refresh triggered for restored execution data');
    setFetchingLogs(true);
    setFetchingStatus(true);
    
    try {
      await Promise.all([
        fetchLiveLogs(executionArn),
        fetchExecutionStatus(executionArn)
      ]);
      
      toast.success('Logs refreshed successfully');
    } catch (err) {
      console.error('Error during manual refresh:', err);
      toast.error('Failed to refresh logs');
    } finally {
      setFetchingLogs(false);
      setFetchingStatus(false);
    }
  };

  // Modified polling setup to handle restored logs properly
  useEffect(() => {
    if (!executionArn) return;
    
    console.log('Setting up execution monitoring for ARN:', executionArn);
    console.log('Data restored from logs:', restoredFromLogs);
    
    // Always do initial fetch to get current state
    if (!restoredFromLogs) {
      fetchLiveLogs(executionArn);
      fetchExecutionStatus(executionArn);
    } else {
      console.log('Data restored from logs - performing one-time refresh to get current state');
      setTimeout(() => {
        fetchLiveLogs(executionArn);
        fetchExecutionStatus(executionArn);
      }, 1000);
    }
    
    let logsIntervalId, statusIntervalId;
    
    // Setup logs polling
    if (logsPollingActive && !restoredFromLogs) {
      logsIntervalId = setInterval(() => {
        fetchLiveLogs(executionArn);
      }, 10000); // Poll every 10 seconds
    }
    
    // Setup status polling
    if (statusPollingActive && !restoredFromLogs) {
      statusIntervalId = setInterval(() => {
        fetchExecutionStatus(executionArn);
      }, 10000); // Poll every 10 seconds
    }
    
    return () => {
      if (logsIntervalId) clearInterval(logsIntervalId);
      if (statusIntervalId) clearInterval(statusIntervalId);
    };
  }, [executionArn, agent?.agentNumber, parentAgent, executingEnvironment, statusPollingActive, logsPollingActive, restoredFromLogs]);

  // Analysis polling setup
  useEffect(() => {
    if (!analysisExecutionArn) return;
    
    fetchAnalysisStatus(analysisExecutionArn);
    
    let analysisIntervalId;
    
    if (analysisPollingActive) {
      analysisIntervalId = setInterval(() => {
        fetchAnalysisStatus(analysisExecutionArn);
      }, 5000);
    }
    
    return () => {
      if (analysisIntervalId) clearInterval(analysisIntervalId);
    };
  }, [analysisExecutionArn, agent?.agentNumber, parentAgent, analysisPollingActive]);

  // Handle form submission
  const handleSubmit = async (env) => {
    if (!validateForm()) return;
  
    setExecutingEnvironment(env);
    setExecuting(true);
    setError(null);
    setResult(null);
    setExecutionArn(null);
    setLiveLogs(null);
    setExecutionStatus(null);
    setExecutionDetails(null);
    setBodyMessage(null);
    setBodyExecutionArn(null);
    setParsedExecutionSummary(null);
    setParsedAgentOutput(null);
    
    // Reset polling states
    setStatusPollingActive(true);
    setLogsPollingActive(true);
    setRestoredFromLogs(false);
  
    try {
      const response = await api.testAgent(agentId, agentType, targetId, formData, env);
    
      if (response) {
        setResult(response.result || response);
        
        // Extract and parse the body message and executionArn
        let parsedBodyMessage = null;
        let parsedBodyExecutionArn = null;
        
        if (response.body && typeof response.body === 'string') {
          try {
            const parsedBody = JSON.parse(response.body);
            parsedBodyMessage = parsedBody.message;
            parsedBodyExecutionArn = parsedBody.executionArn;
          } catch (parseError) {
            console.error('Error parsing response body:', parseError);
          }
        } 
        else if (response.body && typeof response.body === 'object') {
          parsedBodyMessage = response.body.message;
          parsedBodyExecutionArn = response.body.executionArn;
        }
        
        if (!parsedBodyMessage && response.message) {
          parsedBodyMessage = response.message;
        }
        if (!parsedBodyExecutionArn && response.executionArn) {
          parsedBodyExecutionArn = response.executionArn;
        }
        
        if (parsedBodyMessage) {
          setBodyMessage(safeRenderValue(parsedBodyMessage));
        }
        if (parsedBodyExecutionArn) {
          setBodyExecutionArn(safeRenderValue(parsedBodyExecutionArn));
        }

        // Extract executionArn for polling
        let extractedArn = null;
      
        if (response.body && typeof response.body === 'string') {
          try {
            const parsedBody = JSON.parse(response.body);
            if (parsedBody.executionArn) {
              extractedArn = parsedBody.executionArn;
            }
          } catch (parseError) {
            console.error('Error parsing response body:', parseError);
          }
        } 
        else if (response.body && response.body.executionArn) {
          extractedArn = response.body.executionArn;
        } 
        else if (response.executionArn) {
          extractedArn = response.executionArn;
        }
      
        if (extractedArn) {
          console.log('Execution ARN extracted:', extractedArn);
          setExecutionArn(extractedArn);
          fetchLiveLogs(extractedArn);
          fetchExecutionStatus(extractedArn);
        } else {
          console.warn('Could not extract executionArn from response:', response);
        }
      
        toast.success(`Execution in ${env} initiated successfully`);
      }
    } catch (err) {
      console.error('Error executing agent:', err);
      setError(`Failed to execute agent: ${err.message}`);
      toast.error('Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  // Helper function to get the status badge variant based on status
  const getStatusBadgeVariant = (status) => {
    if (!status) return 'secondary';
    
    if (typeof status === 'object' && status.status) {
      status = status.status;
    }
    
    if (typeof status === 'string') {
      status = status.toUpperCase();
      
      if (status === 'RUNNING' || status === 'IN_PROGRESS') return 'primary';
      if (status === 'SUCCEEDED') return 'success';
      if (status === 'FAILED') return 'danger';
      if (status === 'TIMED_OUT') return 'warning';
      if (status === 'ABORTED') return 'warning';
    }
    
    return 'secondary';
  };

  // Format the execution duration
  const formatDuration = (startDate, stopDate) => {
    if (!startDate || !stopDate) return 'In progress';
    
    try {
      const start = new Date(startDate);
      const stop = new Date(stopDate);
      const durationMs = stop - start;
      
      if (durationMs >= 60000) {
        const minutes = Math.floor(durationMs / 60000);
        const seconds = Math.floor((durationMs % 60000) / 1000);
        return `${minutes}m ${seconds}s`;
      }
      
      const seconds = Math.floor(durationMs / 1000);
      return `${seconds}s`;
    } catch (e) {
      return 'Unknown';
    }
  };

  // Navigation handlers
  const handleGoBackToAgent = () => {
    if (microAgentId) {
      navigate(`/agents/${agentId}/micro-agents/${microAgentId}`);
    } else if (miniAgentId) {
      navigate(`/agents/${agentId}/mini-agents/${miniAgentId}`);
    } else {
      navigate(`/agents/${agentId}`);
    }
  };

  const handleGoBackToParentAgent = () => {
    navigate(`/parent/${agentId}`);
  };

  const handleViewLogs = () => {
    if (microAgentId) {
      navigate(`/agents/${agentId}/micro-agents/${microAgentId}/logs`);
    } else if (miniAgentId) {
      navigate(`/agents/${agentId}/mini-agents/${miniAgentId}/logs`);
    }
  };

  // Fetch the agent details with robust parent agent fetching
  const fetchAgentDetails = async () => {
    try {
      setLoading(true);
      
      console.log(`Fetching agent details for targetId: ${targetId}`);
      
      const response = await api.getMicroAgentsbyId(targetId);

      if (response && response.microAgents && response.microAgents.length > 0) {
        const agentData = response.microAgents[0];
        
        console.log('Found agent data:', agentData);
        
        setAgent({
          id: agentData.uniqueIdentifier || targetId,
          name: agentData.microAgentName || 'Unnamed Agent',
          description: agentData.microAgentDescription || 'No description available',
          agentNumber: agentData.agentNumber || '000',
          parentAgentName: agentData.parentAgentName || 'Unknown',
          commissioned: agentData.commissioned || false,
          deployedInDev: agentData.deployedInDev || false,
          deployedInProd: agentData.deployedInProd || false
        });
        
        await fetchParentAgentDetails(agentData);
        
      } else {
        throw new Error('Agent not found in response');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching agent details:', err);
      setError('Failed to load agent details');
      setLoading(false);
    }
  };

  // Robust parent agent fetching with multiple fallback strategies
  const fetchParentAgentDetails = async (agentData) => {
    try {
      console.log('Fetching parent agent details for:', agentData.parentAgentName);
      
      const agentsResponse = await api.getAgents();
      let parentAgentData = null;
      
      console.log('Agents response structure:', agentsResponse);
      
      let agentsList = [];
      if (agentsResponse && agentsResponse.body && agentsResponse.body.agents) {
        agentsList = agentsResponse.body.agents;
      } else if (agentsResponse && agentsResponse.agents) {
        agentsList = agentsResponse.agents;
      } else if (agentsResponse && Array.isArray(agentsResponse.data)) {
        agentsList = agentsResponse.data;
      }
      
      console.log(`Found ${agentsList.length} agents to search through`);
      
      // Try exact match
      parentAgentData = agentsList.find(a => 
        a.uniqueIdentifier === agentData.parentAgentName || 
        a.agentName === agentData.parentAgentName || 
        a.repoName === agentData.parentAgentName
      );
      
      // Try case-insensitive match
      if (!parentAgentData && agentData.parentAgentName) {
        const parentNameLower = agentData.parentAgentName.toLowerCase();
        parentAgentData = agentsList.find(a => 
          (a.agentName && a.agentName.toLowerCase() === parentNameLower) ||
          (a.repoName && a.repoName.toLowerCase() === parentNameLower) ||
          (a.uniqueIdentifier && a.uniqueIdentifier.toLowerCase() === parentNameLower)
        );
      }
      
      // Try partial match
      if (!parentAgentData && agentData.parentAgentName) {
        const parentNameLower = agentData.parentAgentName.toLowerCase();
        parentAgentData = agentsList.find(a => 
          (a.agentName && a.agentName.toLowerCase().includes(parentNameLower)) ||
          (a.repoName && a.repoName.toLowerCase().includes(parentNameLower))
        );
      }
      
      // Look for "AgentDeveloper" as fallback
      if (!parentAgentData) {
        console.log('No specific parent found, looking for AgentDeveloper...');
        parentAgentData = agentsList.find(a => 
          (a.agentName && a.agentName.toLowerCase() === 'agentdeveloper') ||
          (a.repoName && a.repoName.toLowerCase() === 'agentdeveloper') ||
          a.uniqueIdentifier === '1'
        );
      }
      
      // Use the first agent as fallback
      if (!parentAgentData && agentsList.length > 0) {
        console.log('Using first available agent as parent fallback');
        parentAgentData = agentsList[0];
      }
      
      if (parentAgentData) {
        console.log('Found parent agent data:', parentAgentData);
        
        setParentAgent({
          id: parentAgentData.uniqueIdentifier,
          name: parentAgentData.agentName || parentAgentData.repoName || agentData.parentAgentName,
          awsAccount: parentAgentData.awsAccount || parentAgentData.awsAccountNumber || "851725399969",
          awsRegion: parentAgentData.awsRegion || "us-west-2",
          repoAccount: parentAgentData.repoAccount || "materassi",
          repoName: parentAgentData.repoName || "AgentDeveloper",
          agentName: parentAgentData.agentName,
          description: parentAgentData.agentDescription || parentAgentData.description
        });
        
        console.log('Successfully set parent agent:', parentAgentData.agentName || parentAgentData.repoName);
      } else {
        console.warn('Could not find any parent agent, creating fallback');
        
        setParentAgent({
          id: "1",
          name: "AgentDeveloper",
          awsAccount: "851725399969",
          awsRegion: "us-west-2",
          repoAccount: "materassi",
          repoName: "AgentDeveloper",
          agentName: "AgentDeveloper",
          description: "Default parent agent (fallback)"
        });
        
        console.warn('Using fallback parent agent - this may cause issues with API calls');
      }
      
    } catch (err) {
      console.error('Error fetching parent agent details:', err);
      
      console.log('Error occurred, setting default parent agent');
      
      setParentAgent({
        id: "1",
        name: "AgentDeveloper", 
        awsAccount: "851725399969",
        awsRegion: "us-west-2",
        repoAccount: "materassi",
        repoName: "AgentDeveloper",
        agentName: "AgentDeveloper",
        description: "Default parent agent (error fallback)"
      });
    }
  };

  // Render Quick Edit/Fix Section
  const renderQuickEditSection = () => {
    if (!showQuickEditSection) return null;

    return (
      <Card className="logs-card mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>
              <FaWrench className="me-2 text-success" />
              Quick Edit / Fix
              {submittingQuickEdit && <FaSpinner className="fa-spin ms-2" />}
              {quickEditResult?.success && !submittingQuickEdit && (
                <Badge bg="success" className="ms-2">Complete</Badge>
              )}
              {quickEditResult && !quickEditResult.success && !submittingQuickEdit && (
                <Badge bg="danger" className="ms-2">Failed</Badge>
              )}
            </h2>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowQuickEditSection(false)}
              disabled={submittingQuickEdit}
            >
              <FaTimes />
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ minHeight: '300px', maxHeight: '70vh', overflowY: 'auto' }}>
          {quickEditResult?.success && (
            <Alert variant="success" className="mb-3">
              <FaCheckCircle className="me-2" />
              {quickEditResult.message}
              {quickEditResult.executionArn && (
                <div className="mt-2">
                  <small className="text-muted">
                    Execution ARN: {quickEditResult.executionArn}
                  </small>
                </div>
              )}
            </Alert>
          )}

          {quickEditResult && !quickEditResult.success && (
            <Alert variant="danger" className="mb-3">
              <strong>Error:</strong> {quickEditResult.error}
            </Alert>
          )}
          
          {submittingQuickEdit && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center">
                <FaClock className="me-2" />
                <div>
                  <strong>Processing your request...</strong>
                  <div className="small">
                    Executing quick edit/fix instructions. This may take a few moments to complete.
                  </div>
                </div>
              </div>
            </Alert>
          )}
          
          {!quickEditResult?.success && !submittingQuickEdit && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label style={{ paddingLeft: '12px' }}>Instructions *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={quickEditInstructions}
                  onChange={(e) => setQuickEditInstructions(e.target.value)}
                  placeholder="Enter detailed instructions for what changes to make to the agent..."
                  disabled={submittingQuickEdit}
                  required
                />
                <Form.Text className="text-muted" style={{ paddingLeft: '12px' }}>
                  {quickEditInstructions.length}/10 characters minimum
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Include Execution analysis with AI"
                  checked={includeExecutionAnalysis}
                  onChange={(e) => setIncludeExecutionAnalysis(e.target.checked)}
                  disabled={submittingQuickEdit || !analysisResult?.agentOutput}
                  style={{ paddingLeft: '12px' }}
                />
                <Form.Text className="text-muted" style={{ paddingLeft: '12px' }}>
                  {!analysisResult?.agentOutput && 'Execution analysis must be completed first to use this option'}
                </Form.Text>
              </Form.Group>
              
              {agent && parentAgent && (
                <Alert variant="info" className="mb-3">
                  <h6 style={{ paddingLeft: '12px' }}>Target Information:</h6>
                  <div style={{ paddingLeft: '12px' }}><strong>Agent Number:</strong> {agent.agentNumber}</div>
                  <div style={{ paddingLeft: '12px' }}><strong>Agent Name:</strong> {agent.name}</div>
                  <div style={{ paddingLeft: '12px' }}><strong>Parent Agent:</strong> {parentAgent.name}</div>
                  <div style={{ paddingLeft: '12px' }}><strong>GitHub Repository:</strong> {parentAgent.repoAccount}/{parentAgent.repoName}</div>
                </Alert>
              )}

              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  onClick={handleQuickEditSubmit}
                  disabled={submittingQuickEdit || quickEditInstructions.trim().length < 10}
                >
                  {submittingQuickEdit ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <FaWrench className="me-2" />
                      Submit Quick Edit / Fix
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}

          {quickEditResult?.success && !submittingQuickEdit && (
            <div className="text-center py-4">
              <FaCheckCircle size={48} className="text-success mb-3" />
              <h5 className="text-success mb-3">Request Processed Successfully!</h5>
              <p className="lead mb-3">{quickEditResult.message}</p>
              {quickEditResult.executionArn && (
                <div className="mb-3">
                  <small className="text-muted">
                    Execution ARN: {quickEditResult.executionArn}
                  </small>
                </div>
              )}
              <div className="text-muted">
                <small>This section will close automatically in a few moments...</small>
              </div>
            </div>
          )}

          {!quickEditResult && !submittingQuickEdit && !quickEditInstructions.trim() && (
            <div className="text-center py-4">
              <FaWrench size={48} className="text-muted mb-3" />
              <p>Provide detailed instructions for what changes or fixes you'd like to make to this agent.</p>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Enhanced execution result section with parsed live logs output
  const renderExecutionResultWithLogs = () => {
    if (!executionStatus || (executionStatus.status !== 'SUCCEEDED' && !restoredFromLogs)) {
      return null;
    }

    return (
      <Card className="result-card mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>
              Execution Result
            </h2>
            {loadingExecutionDetails && (
              <div className="text-muted">
                <Spinner size="sm" className="me-2" />
                Loading results...
              </div>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {(parsedExecutionSummary || parsedAgentOutput) && (
            <div className="parsed-output-section mb-4">
              <h5 className="mb-3" style={{ paddingLeft: '12px' }}>
                <FaEye className="me-2 text-primary" />
                Execution Output {restoredFromLogs ? '(from Execution History)' : '(from Live Logs)'}
              </h5>
              
              {parsedAgentOutput && (
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <span className="me-2 text-success fw-bold" style={{ paddingLeft: '12px' }}>Agent Output:</span>
                  </div>
                  <div className="p-3 border border-success rounded bg-light">
                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {safeRenderValue(parsedAgentOutput)}
                    </pre>
                  </div>
                </div>
              )}

              {parsedExecutionSummary && (
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <span className="me-2 text-info fw-bold" style={{ paddingLeft: '12px' }}>Execution Summary:</span>
                  </div>
                  <div className="p-3 border border-info rounded bg-light">
                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {safeRenderValue(parsedExecutionSummary)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {executionDetails && (
            <div className="key-fields-section mb-4">
              <h5 className="mb-3" style={{ paddingLeft: '12px' }}>
                <FaEye className="me-2 text-primary" />
                Execution Summary
              </h5>
              
              <div className="mb-4">
                <div className="d-flex align-items-center mb-2">
                  <FaCheckCircle className="me-2 text-success" />
                  <h6 className="mb-0 text-success" style={{ paddingLeft: '12px' }}>Status</h6>
                </div>
                <div className="p-3 border border-success rounded bg-light">
                  <div className="mb-2">
                    <strong>Status:</strong> <Badge bg="success">{executionDetails.status}</Badge>
                  </div>
                  <div className="mb-0">
                    <strong>Message:</strong> {executionDetails.message}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Render Analysis Section
  const renderAnalysisSection = () => {
    if (!showAnalysisSection) return null;

    return (
      <Card className="logs-card mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>
              Execution Analysis with AI
              {fetchingAnalysisStatus && analysisPollingActive && <FaSpinner className="fa-spin ms-2" />}
              {!analysisPollingActive && analysisResult && (
                <Badge bg={analysisResult.success ? 'success' : 'danger'} className="ms-2">
                  {analysisResult.success ? 'Complete' : 'Failed'}
                </Badge>
              )}
            </h2>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setShowAnalysisSection(false)}
            >
              <FaTimes />
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ minHeight: '300px', maxHeight: '70vh', overflowY: 'auto' }}>
          {fetchingAnalysis && (
            <div className="text-center py-4">
              <Spinner />
              <p className="mt-3">Initiating analysis...</p>
            </div>
          )}

          {analysisExecutionArn && !analysisResult && analysisPollingActive && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center">
                <FaClock className="me-2" />
                <div>
                  <strong>Analysis in progress</strong>
                  <div className="small">
                    Analyzing execution: {executionArn?.split(':').pop()}
                  </div>
                  {analysisExecutionArn && (
                    <div className="small text-muted mt-1">
                      Analysis Execution ID: {analysisExecutionArn.split(':').pop()}
                    </div>
                  )}
                  {fetchingAnalysisStatus && analysisPollingActive && (
                    <div className="small">
                      <FaSpinner className="fa-spin me-1" />
                      Checking analysis status...
                    </div>
                  )}
                </div>
              </div>
            </Alert>
          )}

          {analysisResult && (
            <div>
              {analysisResult.success ? (
                <div>
                  <Alert variant="success" className="mb-3">
                    <FaCheckCircle className="me-2" />
                    Analysis completed successfully!
                  </Alert>
                  
                  {analysisResult.agentOutput && (
                    <div className="mb-4">
                      <h5 className="mb-3" style={{ paddingLeft: '12px' }}>
                        Agent Analysis Output
                      </h5>
                      <div className="p-3 bg-light rounded border">
                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {safeRenderValue(analysisResult.agentOutput)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {analysisResult.executionSummary && (
                    <div className="mb-4">
                      <h5 className="mb-3" style={{ paddingLeft: '12px' }}>
                        Analysis Summary
                      </h5>
                      <div className="p-3 bg-light rounded border">
                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {safeRenderValue(analysisResult.executionSummary)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Alert variant="danger">
                  <FaTimes className="me-2" />
                  Analysis failed: {analysisResult.error}
                </Alert>
              )}
            </div>
          )}

          {!fetchingAnalysis && !analysisExecutionArn && !analysisResult && (
            <div className="text-center py-4">
              <FaBrain size={48} className="text-muted mb-3" />
              <p>Click "Analyze Execution with Agent" to start AI-powered analysis of the execution.</p>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Render loading state
  if (loading && !agent) {
    return (
      <div className="agent-testing-page loading-container">
        <Spinner />
        <p>Loading agent details...</p>
      </div>
    );
  }

  return (
    <div className="agent-testing-page">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header with Agent Info */}
      <div className="agent-header">
        <div className="agent-title-container">
          <h1 className="agent-title">Test {agentTypeLabel}</h1>
          <div className="agent-subtitle-wrapper">
            <p className="agent-subtitle">
              Agent ID: {agent?.id || targetId}
              {agent?.agentNumber && <span> | Number: {agent.agentNumber}</span>}
              {environment && <span> | Environment: {environment}</span>}
            </p>
            {restoredFromLogs && (
              <div className="mt-2">
                <Badge bg="info">DATA RESTORED FROM EXECUTION LOGS</Badge>
              </div>
            )}
          </div>
        </div>
        
        <div className="agent-actions">
          <Button 
            variant="outline-primary" 
            onClick={handleGoBackToAgent}
            className="action-button"
            style={{
              minWidth: '160px',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '8px',
              paddingBottom: '8px',
              whiteSpace: 'nowrap',
              fontSize: '0.875rem'
            }}
          >
            <FaArrowLeft className="me-2" />
            Develop Agent
          </Button>

          <Button 
            variant="outline-primary" 
            onClick={handleGoBackToParentAgent}
            className="action-button"
            style={{
              minWidth: '160px',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '8px',
              paddingBottom: '8px',
              whiteSpace: 'nowrap',
              fontSize: '0.875rem'
            }}
          >
            <FaArrowLeft className="me-2" />
            Back to Parent Agent
          </Button>
          
          <Button 
            variant="outline-primary" 
            onClick={handleViewLogs}
            className="action-button"
            style={{
              minWidth: '160px',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '8px',
              paddingBottom: '8px',
              whiteSpace: 'nowrap',
              fontSize: '0.875rem'
            }}
          >
            <FaHistory className="me-2" />
            View logs
          </Button>
        </div>
      </div>
      
      {/* Agent Details Card */}
      {agent && (
        <Card className="agent-info-card mb-4">
          <Card.Header>
            <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>Agent Information</h2>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="agent-info-details">
                  <div className="info-item">
                    <div className="info-label">Name:</div>
                    <div className="info-value">{agent.name}</div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Description:</div>
                    <div className="info-value">{agent.description}</div>
                  </div>
                </div>
              </Col>
              
              <Col md={6}>
                <div className="agent-info-details">
                  <div className="info-item">
                    <div className="info-label">Agent Number:</div>
                    <div className="info-value">{agent.agentNumber}</div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Commissioned:</div>
                    <div className="info-value">
                      <Badge bg={agent.commissioned ? 'success' : 'secondary'}>
                        {agent.commissioned ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Development Status:</div>
                    <div className="info-value">
                      <Badge bg={agent.deployedInDev ? 'success' : 'secondary'}>
                        {agent.deployedInDev ? 'Deployed' : 'Not Deployed'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">Production Status:</div>
                    <div className="info-value">
                      <Badge bg={agent.deployedInProd ? 'success' : 'secondary'}>
                        {agent.deployedInProd ? 'Deployed' : 'Not Deployed'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            
            {!parentAgent && (
              <Alert variant="warning" className="mt-3">
                <FaInfoCircle className="me-2" />
                <strong>Warning:</strong> Parent agent information could not be loaded. Some functionality may be limited.
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}
      
      {/* Input Parameters Card */}
      <Card className="test-card mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>Input Parameters</h2>
          </div>
        </Card.Header>
        
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <FaInfoCircle className="me-2" />
              {error}
            </Alert>
          )}
          
          <Form>
            <Row>
              {formFields.map(field => {
                if (field.isGroup) {
                  return (
                    <Col md={12} key={field.name} className="mb-3">
                      <h5 className="text-primary border-bottom pb-2" style={{ paddingLeft: '12px' }}>
                        {field.label}
                      </h5>
                      {field.description && (
                        <p className="text-muted small mb-3" style={{ paddingLeft: '12px' }}>{field.description}</p>
                      )}
                    </Col>
                  );
                }
                
                // UPDATED: Make text boxes bigger for certain fields
                const isLarge = isLargeTextField(field.name) || field.format === 'textarea';
                const columnSize = isLarge ? 12 : 6;
                
                return (
                  <Col md={columnSize} key={field.name} className="mb-3">
                    <Form.Group>
                      <Form.Label className={field.required ? 'required-field' : ''} style={{ paddingLeft: '12px' }}>
                        {field.label}
                      </Form.Label>
                      
                      {renderFormField(field, formData, handleInputChange, formErrors, getNestedValue)}
                      
                      {field.description && (
                        <Form.Text className="field-description" style={{ paddingLeft: '12px' }}>
                          <FaInfoCircle className="me-1" />
                          {field.description}
                        </Form.Text>
                      )}
                      
                      {formErrors[field.name] && (
                        <div className="field-error" style={{ paddingLeft: '12px' }}>{formErrors[field.name]}</div>
                      )}
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>
            
            <div className="execute-buttons">
              <Button
                variant="primary"
                onClick={() => handleSubmit('development')}
                disabled={executing}
                className="execute-btn"
              >
                {executing && executingEnvironment === 'development' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Running in Dev...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Execute in Development
                  </>
                )}
              </Button>
              
              <Button
                variant="danger"
                onClick={() => handleSubmit('production')}
                disabled={executing}
                className="execute-btn"
              >
                {executing && executingEnvironment === 'production' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Running in Prod...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Execute in Production
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Execution Status Section */}
      {(executionArn || bodyMessage || bodyExecutionArn || restoredFromLogs) && (
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>
                Execution Status
                {fetchingStatus && statusPollingActive && <FaSpinner className="fa-spin ms-2" />}
              </h2>
              
              <div className="d-flex gap-2">
                {/* Analyze Execution Button - uses micro-agent 128 */}
                <Button 
                  variant="warning" 
                  size="sm"
                  onClick={handleAnalyzeExecution}
                  disabled={fetchingAnalysis || !executionArn}
                  style={{ 
                    backgroundColor: '#ffc107', 
                    borderColor: '#ffc107', 
                    color: '#000',
                    fontWeight: 'bold',
                    minWidth: '160px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem'
                  }}
                >
                  {fetchingAnalysis ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Starting...
                    </>
                  ) : (
                    'Analyze Execution'
                  )}
                </Button>

                {/* Quick Edit/Fix Button */}
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={() => {
                    setQuickEditInstructions('');
                    setIncludeExecutionAnalysis(false);
                    setQuickEditResult(null);
                    setShowQuickEditSection(true);
                  }}
                  disabled={!executionArn && !restoredFromLogs}
                  style={{ 
                    backgroundColor: '#28a745', 
                    borderColor: '#28a745', 
                    color: '#fff',
                    fontWeight: 'bold',
                    minWidth: '160px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem'
                  }}
                >
                  Quick Edit / Fix
                </Button>

                {/* ADDED: Refresh button for restored logs */}
                {restoredFromLogs && (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={handleManualRefresh}
                    disabled={fetchingStatus}
                    title="Refresh to get the latest execution state"
                    style={{
                      minWidth: '160px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem'
                    }}
                  >
                    {fetchingStatus ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <FaSync className="me-2" />
                        Refresh
                      </>
                    )}
                  </Button>
                )}

                {/* ADDED: Stop Execution Button - matching AgentTestingPage implementation */}
                {executionArn && statusPollingActive && 
                 executionStatus?.status !== 'SUCCEEDED' && 
                 executionStatus?.status !== 'FAILED' && (
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={handleStopExecution}
                    disabled={stoppingExecution}
                    title="Stop the current execution"
                    style={{
                      minWidth: '160px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem'
                    }}
                  >
                    {stoppingExecution ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Stopping...
                      </>
                    ) : (
                      <>
                        <FaStop className="me-2" />
                        Stop Execution
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {(bodyMessage || bodyExecutionArn) && (
              <div className="execution-result-fields mb-4">
                <Row>
                  {bodyMessage && (
                    <Col md={12} className="mb-3">
                      <div className="info-field">
                        <div className="info-field-label">
                          <FaCheckCircle className="me-2 text-success" />
                          <strong>Status Message:</strong>
                        </div>
                        <div className="info-field-value">
                          <div className="p-3 bg-light rounded border">
                            {safeRenderValue(bodyMessage)}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
            
            {executionStatus && (
              <Row>
                <Col md={4}>
                  <div className="info-item mb-3">
                    <strong>Status: </strong>{' '}
                    <Badge 
                      bg={getStatusBadgeVariant(executionStatus?.status)}
                      style={{ fontSize: '1rem', padding: '0.5rem 0.75rem' }}
                    >
                      {executionStatus?.status || 'RUNNING'}
                    </Badge>
                  </div>
                </Col>
                
                <Col md={4}>
                  <div className="info-item mb-3">
                    <strong>Start: </strong>{' '}
                    {executionStatus?.startDate ? new Date(executionStatus.startDate).toLocaleString() : 'Pending...'}
                  </div>
                </Col>
                
                <Col md={4}>
                  <div className="info-item mb-3">
                    <strong>Duration: </strong>{' '}
                    {executionStatus?.startDate && executionStatus?.stopDate 
                      ? formatDuration(executionStatus.startDate, executionStatus.stopDate)
                      : executionStatus?.duration ? `${executionStatus.duration}s` : 'In progress...'}
                  </div>
                </Col>
              </Row>
            )}
            
            {executionStatus?.status === 'FAILED' && (
              <Alert variant="danger" className="mt-3">
                <strong>Execution Failed</strong>
                {executionStatus.error && (
                  <div className="mt-2">
                    <strong>Error:</strong> {safeRenderValue(executionStatus.error)}
                  </div>
                )}
              </Alert>
            )}
            
            {executionStatus?.status === 'SUCCEEDED' && (
              <Alert variant="success" className="mt-3">
                <strong>Execution Completed Successfully</strong>
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}
      
      {/* Enhanced Execution Result Section */}
      {renderExecutionResultWithLogs()}
      
      {/* Live Execution Logs - UPDATED to show task executions table when available */}
      {(executionArn || restoredFromLogs) && (
        <Card className="logs-card">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="section-heading text-dark" style={{ paddingLeft: '12px' }}>
                Live Execution Logs
                {fetchingLogs && logsPollingActive && <FaSpinner className="fa-spin ms-2" />}
                {!logsPollingActive && (
                  <Badge bg="secondary" className="ms-2">
                    {restoredFromLogs ? 'Monitoring Stopped' : 'Monitoring Stopped'}
                  </Badge>
                )}
              </h2>
              <div className="d-flex gap-2">
                {restoredFromLogs && (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={handleManualRefresh}
                    disabled={fetchingLogs || fetchingStatus}
                    title="Refresh to get the latest execution state"
                    style={{
                      minWidth: '160px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem'
                    }}
                  >
                    {(fetchingLogs || fetchingStatus) ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <FaSync className="me-2" />
                        Refresh Logs
                      </>
                    )}
                  </Button>
                )}
                
                {executionArn && statusPollingActive && executionStatus?.status !== 'SUCCEEDED' && executionStatus?.status !== 'FAILED' && (
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={handleStopExecution}
                    disabled={stoppingExecution}
                    title="Stop the current execution"
                    style={{
                      minWidth: '160px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem'
                    }}
                  >
                    {stoppingExecution ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Stopping...
                      </>
                    ) : (
                      <>
                        <FaStop className="me-2" />
                        Stop Execution
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="logs-content">
              {/* Check if we have task execution data to show in table format */}
              {(() => {
                const taskExecutions = parseTaskExecutions(liveLogs);
                
                if (taskExecutions.length > 0) {
                  // Show task executions table
                  return (
                    <div className="p-3">
                      <TaskExecutionsTable taskExecutions={taskExecutions} />
                    </div>
                  );
                } else if (restoredFromLogs && liveLogs) {
                  // Show static logs data when restored from LogsReview and no task data
                  return (
                    <div className="p-3">
                      <div className="p-3 bg-light rounded" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {safeRenderValue(liveLogs)}
                        </pre>
                      </div>
                    </div>
                  );
                } else {
                  // Show live execution logs window
                  return (
                    <ExecutionLogsWindow 
                      executionArn={executionArn}
                      agentId={agentId}
                      targetType={agentType}
                      result={liveLogs}
                      refreshInterval={10000}
                    />
                  );
                }
              })()}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Analysis Section */}
      {renderAnalysisSection()}

      {/* Quick Edit/Fix Section */}
      {renderQuickEditSection()}
    </div>
  );
};

// Helper function to render form fields based on their type
const renderFormField = (field, formData, handleInputChange, formErrors, getNestedValue) => {
  const value = getNestedValue(formData, field.name) || '';
  const isInvalid = !!formErrors[field.name];
  
  // UPDATED: Make certain fields larger (main task, context, instructions, etc.)
  const isLargeField = field.name.toLowerCase().includes('instructions') ||
                      field.name.toLowerCase().includes('architecture') ||
                      field.name.toLowerCase().includes('description') ||
                      field.name.toLowerCase().includes('task') ||
                      field.name.toLowerCase().replace(/[_\s]/g, '').includes('maintask') ||
                      field.name.toLowerCase().replace(/[_\s]/g, '').includes('main_task') ||
                      field.name.toLowerCase().includes('context') ||
                      field.format === 'textarea' ||
                      field.name.toLowerCase().includes('prompt');
  
  switch (field.type) {
    case 'boolean':
      return (
        <Form.Check
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => handleInputChange(field.name, e.target.checked)}
          isInvalid={isInvalid}
          label={field.label}
          style={{ paddingLeft: '12px' }}
        />
      );
      
    case 'number':
    case 'integer':
      return (
        <Form.Control
          type="number"
          value={value}
          onChange={(e) => handleInputChange(field.name, e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value)}
          isInvalid={isInvalid}
          placeholder={`Enter ${field.label}`}
          step={field.type === 'integer' ? 1 : 'any'}
        />
      );
      
    case 'array':
      return (
        <Form.Control
          as="textarea"
          rows={4}
          value={Array.isArray(value) ? value.join('\n') : value}
          onChange={(e) => handleInputChange(field.name, e.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
          isInvalid={isInvalid}
          placeholder="Enter one item per line"
        />
      );
      
    case 'object':
      return (
        <Form.Control
          as="textarea"
          rows={8}
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleInputChange(field.name, parsed);
            } catch (err) {
              handleInputChange(field.name, e.target.value);
            }
          }}
          isInvalid={isInvalid}
          placeholder={`Enter ${field.label} as JSON`}
          className="json-input"
        />
      );
      
    default:
      if (field.enum) {
        return (
          <Form.Select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
          >
            <option value="">Select {field.label}</option>
            {field.enum.map((option, index) => (
              <option key={index} value={option}>
                {field.options && field.options[index] ? field.options[index] : option}
              </option>
            ))}
          </Form.Select>
        );
      }
      
      if (isLargeField) {
        return (
          <Form.Control
            as="textarea"
            rows={12}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            placeholder={`Enter ${field.label}`}
          />
        );
      }
      
      return (
        <Form.Control
          type={field.format === 'email' ? 'email' : field.format === 'password' ? 'password' : 'text'}
          value={value}
          onChange={(e) => handleInputChange(field.name, e.target.value)}
          isInvalid={isInvalid}
          placeholder={`Enter ${field.label}`}
        />
      );
  }
};

export default MiniAgentTestingPage;